import Ember from 'ember';
import Route from 'cio/routes/base';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import { isNone } from '@ember/utils';
import Transition from '@ember/routing/-private/transition';
import { ModelRegistry } from 'ember-data/model';
import DS from 'ember-data';
import AuthService from 'cio/services/auth';
import MasqService from 'cio/services/masq';
import EnvironmentService from 'cio/services/environment';
import MetricsService from 'cio/services/metrics';
import RavenService from 'cio/services/raven';
import EnvironmentModel from 'cio/models/environment';
import ApiService, { ApiError, ApiResponse } from 'cio/services/api';
import config from 'cio/config/environment';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import SettingsService from 'cio/services/settings';
import { updateChannelStateForMeasure, updateConfigurableDenominators } from 'cio/utils/metrics/measure';
import CustomFormsService from 'cio/services/custom-forms';
import SubscriptionCenterService from 'cio/services/subscription-center';
import { redirectAwayAndAlert } from 'cio/utils/route-access';
import BackfillsService from 'cio/services/backfills';
import RouterService from '@ember/routing/router-service';
import HydraService from 'cio/services/hydra';
import AccountService from 'cio/services/account';
import { announceRefreshAuth } from '@customerio/hydra-nav-state';
import EnvironmentController from './controller';
import isDestroyed from 'cio/utils/is-destroyed';
import FeaturesService from 'ember-feature-flags/services/features';
import { serializeToJSONAPI } from 'cio/utils/models/json-api';
import BillingService from 'cio/services/billing';

// These are the list of models that need to be or can be persisted across workspaces.
const modelsToKeep: (keyof ModelRegistry)[] = [
  'account',
  'account-usage',
  'api-credential',
  'billing-info',
  'coupon',
  'environment',
  'ext-api-key',
  'plan',
  'receipt',
  'sso-configuration',
  'subscription',
  'tax-rate',
  'tier',
  'user',
  'user-other-account',
];
const environmentReloadInterval = 60 * 1000;

async function waitForStoreQueue(context: any) {
  return new Promise((resolve) => {
    // ember-data uses the `join` method on the store, which is a store-specific
    // queue that other can can be "join"ed to. The `join` method comes from
    // @ember/runloop, which will join the existing queue or create a new one.
    context.store._join(resolve);
  });
}

type EnvironmentRouteParams = {
  environment_id: string;
};

export default class EnvironmentRoute extends Route {
  @service account!: AccountService;
  @service auth!: AuthService;
  @service customForms!: CustomFormsService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service subscriptionCenter!: SubscriptionCenterService;
  @service masq!: MasqService;
  @service environment!: EnvironmentService;
  @service settings!: SettingsService;
  @service('metrics') metricsService!: MetricsService;
  @service raven!: RavenService;
  @service store!: DS.Store;
  @service backfills!: BackfillsService;
  @service router!: RouterService;
  @service hydra!: HydraService;
  @service features!: FeaturesService;
  @service billing!: BillingService;
  @service api!: ApiService;

  get isEnvironmentAccessible() {
    let params = this.paramsFor('environment') as EnvironmentRouteParams;
    return this.auth.user?.canAccessEnvironment(params.environment_id) ?? false;
  }

  async unloadModels() {
    // Serialize records in the format we expect from the back end. Each
    // collection of models is an array with a pluralized model name as the key.
    // We then clear the entire store, re-seed it with the serialized models,
    // then relink a few models to their host services. The `afterModel` hook
    // in this route is responsible for linking the environment model to the
    // service, so we don't need to worry about that here. Other models are
    // requested as needed.
    let currentAccountId = this.account.current.id;
    let currentBillingInfoId = this.billing.billingInfo?.id;
    let currentSubscriptionId = this.billing.subscription?.id;
    let currentPlanSlug = this.billing.paramPlan?.slug;
    let records = modelsToKeep.flatMap((modelName) => {
      let models = this.store.peekAll(modelName).toArray();
      let serializedJSONAPI = models
        .filter((model) => !model.get('isNew'))
        .map((model: DS.Model) => serializeToJSONAPI(this.store, modelName, model));

      return serializedJSONAPI;
    });
    await this.store.unloadAll();
    await waitForStoreQueue(this);
    await Promise.all(records.map((record) => this.store.push(record)));
    await waitForStoreQueue(this);

    // Re-establish the models for the current account and current user
    let account = await this.store.peekRecord('account', currentAccountId);

    if (account == null) {
      throw new Error('Error re-seeding the ember store: could not find previously current account');
    }

    this.account.set('current', account);
    this.auth.set('user', account.currentUser);

    // Reinitialize the billing service without making the call to the back end
    let billingInfoRes: Record<string, any> = {};

    if (currentBillingInfoId != null) {
      billingInfoRes.billing_info = { id: currentBillingInfoId };
    }

    if (currentSubscriptionId != null) {
      billingInfoRes.subscription = { id: currentSubscriptionId };
    }

    this.billing.setBillingProperties(billingInfoRes, currentPlanSlug);

    // Clear outdated data stored in services
    this.metricsService.clearAllMetrics();
    this.facebookLeadAds.unload();
    this.customForms.unload();
    this.subscriptionCenter.unload();
  }

  async beforeModel(transition: Transition) {
    await super.beforeModel(transition);

    if (this.account.planFeatures?.messaging?.expired) {
      // Allow access to these routes even if the trial has expired:
      let allowedRoutes = ['workspace-settings.'];

      if (allowedRoutes.some((allowedRoute) => transition.to.name.startsWith(allowedRoute)) === false) {
        redirectAwayAndAlert(this, transition, {
          redirectTo: 'trial-expired',
          alertMessage: '',
        });
      }
      return;
    }

    if (!this.isEnvironmentAccessible && this.auth.user != null) {
      redirectAwayAndAlert(this, transition, {
        redirectTo: ['environment', this.auth.user.defaultEnvironment as any],
        alertMessage: 'You do not have access to that workspace. Contact an Account Admin to get access.',
      });
    }

    let previousModel = (this.modelFor('environment') as EnvironmentModel) || null;
    let { environment_id: transitioningToEnvironmentId } = this.paramsFor('environment') as EnvironmentRouteParams;

    // If we are switching environments, unload the records from the previous environment
    if (previousModel != null && previousModel.id !== transitioningToEnvironmentId) {
      await this.unloadModels();
      this.backfills.fetch.cancelAll();
    }
  }

  async model(params: { environment_id: string }) {
    return this.store.findRecord('environment', params.environment_id, { reload: true });
  }

  afterModel(model: EnvironmentModel) {
    if (isNone(model)) {
      return;
    }

    this.environment.set('current', model);
    this.environment.set('local', model.id);

    // TODO: Use feature flag as well
    updateConfigurableDenominators(model.metricDenominator);
    updateChannelStateForMeasure('opened', 'email', model.emailOpenTracking);
    updateChannelStateForMeasure('clicked_to_opened', 'email', model.emailOpenTracking);

    this.reloadEnvironment.perform();
    this.setUserLastEnvironmentTask.perform(model);

    // Avoid calling Journeys APIs if the user doesn't have access to Journeys
    if (!this.account.isJourneysAvailable) {
      return;
    }
    this.environment.loadDomains.perform();
    this.settings.loadEnvironmentSettings.perform(true);
    if (this.account.isJourneysActivated) {
      this.backfills.fetch.perform();
    }
  }

  reloadEnvironment = task({ restartable: true }, async () => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (Ember.testing) {
        // Can't have infinite loops with timeouts in acceptance tests
        break;
      }
      await timeout(environmentReloadInterval);
      await this.environment.reload.perform({ isBackgroundTask: true });
    }
  });

  setUserLastEnvironmentTask = task(async (environment: EnvironmentModel) => {
    // In non-hydra mode, we set this when the environment is changed via the
    // workspace dropdown
    if (!this.hydra.isHydraEnabled) {
      return;
    }

    // save the environment to the user model so its loaded on the next sign in
    // skip if env is in a masq'd account: saving it can cause environment/user account mismatch, redirect problems later
    if (this.masq.isOn) {
      return;
    }

    let { currentUser: user } = this.account.current;

    user.set('lastEnvironment', environment);
    try {
      await user.save();

      if (this.hydra.isHydraEnabled) {
        announceRefreshAuth();
      }
    } catch (e) {
      this.apiErrors.report(e);
      user.rollbackAttributes();
    }
  });

  @action
  error(error: ApiResponse) {
    if (config.isDevelopment || Ember.testing) {
      // eslint-disable-next-line no-console
      console.error(error);
    }

    let errors = (error.errors as ApiError[] | null) ?? [];
    let [firstError] = errors;
    let status;

    if (firstError?.status) {
      status = firstError.status;
    } else {
      status = 'unknown';
    }

    // Unauthorised requests are handled by application Ember Data adapter, conditionally cause a logout
    if (status === '401') {
      return;
    }

    // Report any unknown errors (as a whole) to Sentry
    if (status === 'unknown') {
      this.raven.captureException(error as any);
    }

    return this.router.transitionTo(
      `${this.hydra.useNewWorkspacesUrls ? 'environment_content' : 'environment'}.errors`,
      status,
    );
  }

  @action
  loading(transition: Transition, originRoute: Route) {
    let environmentController = this.controllerFor('environment') as EnvironmentController | null;

    // In hydra, when switching to or between workspace settings, we want to
    // show a loading spinner while the environment model is being loaded.
    // In the past we didn't need an environment loading template, because the
    // initial page load had an app loading spinner, and switching environments
    // was done via an ember concurrency task and a fade out/in transition.
    // In Hydra, switching between environment settings is a regular transition,
    // so we need that loading state in this special case.
    if (this.hydra.isHydraEnabled && originRoute === this && environmentController != null) {
      environmentController.showLoadingState = true;

      transition.promise.finally(() => {
        if (environmentController != null && !isDestroyed(environmentController)) {
          environmentController.showLoadingState = false;
        }
      });
    }

    return true;
  }
}
